export const TERMS_OF_SERVICE_URL = "https://evotrux.com/terms";
export const SUPPORT_EMAIL = "support@evotrux.com";
export const BG_ICON_COLOR = "#0d2e45";
export const LIGHT_GREY = "#e0e0e0";
// Mirrors application/shipment/constants/__init__.py
export const MULTI_SHIPMENT_PROJECT_MIN_LOADS_AVAILABLE = 1;
export const MULTI_SHIPMENT_PROJECT_MAX_LOADS_AVAILABLE = 1000;

/**
 * Orders quote states by relevance to the user.
 * The lower the priority number, the higher the importance.
 *
 * For example, for a shipment with multiple quotes, an accepted quote
 * will be at the top of the list of quotes.
 *
 * Consumed by sortQuotes() in frontend/src/utils/quotes.ts
 */
export const QUOTE_STATUS_PRIORITY: Record<InternalQuoteStatusValue, number> = {
  ACCEPTED: 1,
  ACTIVE: 2,
  COUNTERED: 3,
  DECLINED: 4,
  CANCELLED: 5,
  EXPIRED: 6,
  SHIPMENT_LOST: 7,
  SHIPMENT_CANCELLED: 8,
  SHIPMENT_EXPIRED: 9,
};

/**
 * Row sort order for the shipment progress match table, per
 * docs/engineering/design/carrier_no_truck_available_design.md (Outcome 8's
 * precedence): Quoted, then No truck available, then Viewed, then Sent.
 *
 * Consumed by sortByCarrierShipmentProgressStatus() in
 * frontend/src/components/Shipper/components/ShipmentProgress/ShipmentProgressDetail/utils.ts
 */
export const CARRIER_SHIPMENT_PROGRESS_STATUS_PRIORITY: Record<
  CarrierShipmentProgressStatusValue,
  number
> = {
  QUOTED: 0,
  NO_CAPACITY: 1,
  VIEWED: 2,
  SENT: 3,
};

export const QUANTITY_REQUIRED_UNIT_OF_MEASURE_TYPES = [
  "PER_METRIC_TON",
  "PER_SHORT_TON",
  "PER_HOUR",
];

// Origin stamped on chat messages published by the web app. Mirrors the backend
// SourceType so the message webhook can read meta.source directly instead of
// defaulting it. Keep values in sync with utils.models.SourceType.
export const MESSAGE_SOURCE = {
  CUSTOMER_APP: "CUSTOMER_APP",
} as const;

export const QUOTES_ELEMENT_ID = "quotes-element-id";

/**
 * Luzmo's US app server, used only to start preloading its dashboard bundle.
 *
 * The backend owns the authoritative host and sends it as `app_host` in the
 * embed payload; that is what renders a dashboard. A preload happens before any
 * session exists, so it cannot wait for the payload — hence this second copy.
 * Change both together.
 *
 * Passing it is mandatory: `preloadDashboard()` defaults to app.luzmo.com, the
 * EU host, where the US bundle does not exist.
 */
export const LUZMO_APP_HOST = "https://app.us.luzmo.com";

export const EVOTRUX_BLUE = "#1976d2";
export const EVOTRUX_BLUE_LIGHT = "#e3f2fd";
export const EVOTRUX_BLUE_DARK = "#1565c0";
export const EVOTRUX_BLUE_SHADOW = "rgba(25, 118, 210, 0.3)";
export const EVOTRUX_BLUE_SHADOW_HOVER = "rgba(25, 118, 210, 0.4)";
export const EVOTRUX_GREEN = "#2e7d32";
export const EVOTRUX_TEAL = "#0097a7";
export const EVOTRUX_NAVY = "#0F2A3D";
export const EVOTRUX_BRAND_GREEN = "#3ECF8E";
export const EVOTRUX_BRAND_GREEN_HOVER = "#34C77B";
export const EVOTRUX_TOAST_BG = "#D8F7E8";
export const EVOTRUX_TOAST_ICON = "#00B870";
export const EVOTRUX_TOAST_BTN_BORDER = "#009B61";
export const EVOTRUX_TOAST_BTN_TEXT = "#007A4D";
export const EVOTRUX_TOAST_SHADOW = "0 8px 18px rgba(13, 46, 69, 0.14)";
export const EVOTRUX_TOAST_HOVER_BG = "rgba(0, 184, 112, 0.08)";
export const EVOTRUX_SURFACE_WHITE = "#fefefe";
export const EVOTRUX_BORDER_GREY = "#D1D5DB";
export const EVOTRUX_BORDER_GREY_HOVER = "#9CA3AF";
export const EVOTRUX_ERROR_RED = "#D32F2F";
export const EVOTRUX_WARNING_AMBER = "#ED6C02";
export const EVOTRUX_WARNING_AMBER_LIGHT = "#fff3e0";
// Vertical rule that ties an answer back to its question in shipment Q&A.
export const QA_THREAD_BORDER_GREY = "#8d8d8dad";
// Text that reads as a link because it sits inline in a row of other text,
// where a button would be too heavy. Not one of the brand blues above.
export const EVOTRUX_LINK_BLUE = "#2563eb";

// Translucent white over the dark header bar: the search field surface, the
// same surface on hover, and the muted text and icons sitting on it.
export const HEADER_FIELD_BG = "rgba(255, 255, 255, 0.15)";
export const HEADER_FIELD_BG_HOVER = "rgba(255, 255, 255, 0.25)";
export const HEADER_FIELD_MUTED_WHITE = "rgba(255, 255, 255, 0.7)";

// Filled star on a company that has at least one review, and the muted star shown
// when a company has none.
export const REVIEW_STAR_GOLD = "#faaf00";
export const STAT_DISABLED_GREY = "#b5b3b3";
export const STAT_ICON_PURPLE = "#9c27b0";
export const EVOTRUX_FILTER_ACTIVE_INDICATOR = "#9c27b0";
export const EVOTRUX_SIDEBAR_TOGGLE_BG = "#E6E7E8";
export const EVOTRUX_SIDEBAR_TOGGLE_BG_HOVER = "#DADCDE";

// The quote competitiveness score ramp (docs/engineering/design/quote_competitiveness_scoring_design.md
// §9) — a self-contained 4-level system distinct from the app's general brand
// colors, carried over verbatim from the approved Magic Patterns design so it
// isn't re-derived from a similar-but-different existing color.
export const QUOTE_SCORE_VERY_COMPETITIVE = "#047857";
export const QUOTE_SCORE_VERY_COMPETITIVE_TINT = "#ECFDF5";
export const QUOTE_SCORE_VERY_COMPETITIVE_TINT_HOVER = "#D1FAE5";
export const QUOTE_SCORE_COMPETITIVE = "#0E7490";
export const QUOTE_SCORE_COMPETITIVE_TINT = "#ECFEFF";
export const QUOTE_SCORE_COMPETITIVE_TINT_HOVER = "#CFFAFE";
export const QUOTE_SCORE_SOMEWHAT_COMPETITIVE = "#B45309";
export const QUOTE_SCORE_SOMEWHAT_COMPETITIVE_TINT = "#FFFBEB";
export const QUOTE_SCORE_SOMEWHAT_COMPETITIVE_TINT_HOVER = "#FEF3C7";
export const QUOTE_SCORE_NOT_COMPETITIVE = "#A8443A";
export const QUOTE_SCORE_NOT_COMPETITIVE_TINT = "#FBF1EF";
export const QUOTE_SCORE_NOT_COMPETITIVE_TINT_HOVER = "#F6DFDB";
// Terminal (lost/cancelled/etc.) quotes drop to this grey — reuses the
// existing border-grey-hover token, which is already the exact same value.
export const QUOTE_SCORE_MUTED = EVOTRUX_BORDER_GREY_HOVER;
export const QUOTE_SCORE_MUTED_TINT = "#FAFAFA";

// Carrier company logo rules (docs/engineering/design/carrier_company_logo_phase_2_design.md §4).
// Raster only — SVG is excluded because it can carry <script> and there is no
// server-side library to sanitize it.
export const LOGO_ALLOWED_INPUT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
] as const;
export const MAX_LOGO_SIZE_MB = 5;
export const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024;
export const MIN_LOGO_DIMENSION_PX = 100;
export const MAX_LOGO_STORED_DIMENSION_PX = 512;

export const CARRIER_UPGRADE_RELATIVE_URL = "/carrier/upgrade";
export const SHIPPER_UPGRADE_RELATIVE_URL = "/shipper/upgrade";

// Booking link for shippers to schedule a demo / onboarding session with the
// Evotrux sales & ops team (used before a company is granted posting permission).
export const SCHEDULE_ONBOARDING_URL =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2q_WBxc2x4hOcQMVFUsu1QEa7_8xG4KnihHcGRpC4XdG1K7k0ellP4rYl4Qub5m-I3aKCsRoHR";
export const COMMAND_LINK_LANDING_PATH_PREFIX = "/command-link-landing";
export const COMMAND_LINK_LANDING_RELATIVE_URL = `${COMMAND_LINK_LANDING_PATH_PREFIX}/:commandName`;

// URLs rendered only by the unauthenticated Switcher. Once a user is
// authenticated these paths have no matching route, so any code that lands
// on one of them post-login (full-page reload, programmatic navigate) must
// be redirected to '/' to avoid a 404 flash before useLoadPath corrects.
export const AUTH_ONLY_RELATIVE_URLS = [
  "/login",
  "/mfa-challenge",
  "/force-change-password",
  "/sign-up/success",
  "/verify",
  "/forgot-password",
  "/confirm-forgot-password",
  "/voluntell-login",
  "/activate-account",
];

type ProvinceStateValueToLabelMap = Record<string, string>;

export const PROVINCE_STATE_VALUE_TO_LABEL_MAP: ProvinceStateValueToLabelMap = {
  "US-AL": "Alabama",
  "US-AK": "Alaska",
  "CA-AB": "Alberta",
  "US-AZ": "Arizona",
  "US-AR": "Arkansas",
  "CA-BC": "British Columbia",
  "US-CA": "California",
  "US-CO": "Colorado",
  "US-CT": "Connecticut",
  "US-DE": "Delaware",
  "US-DC": "District of Columbia",
  "US-FL": "Florida",
  "US-GA": "Georgia",
  "US-ID": "Idaho",
  "US-IL": "Illinois",
  "US-IN": "Indiana",
  "US-IA": "Iowa",
  "US-KS": "Kansas",
  "US-KY": "Kentucky",
  "US-LA": "Louisiana",
  "US-ME": "Maine",
  "CA-MB": "Manitoba",
  "US-MD": "Maryland",
  "US-MA": "Massachusetts",
  "US-MI": "Michigan",
  "US-MN": "Minnesota",
  "US-MS": "Mississippi",
  "US-MO": "Missouri",
  "US-MT": "Montana",
  "US-NE": "Nebraska",
  "US-NV": "Nevada",
  "CA-NB": "New Brunswick",
  "US-NH": "New Hampshire",
  "US-NJ": "New Jersey",
  "US-NM": "New Mexico",
  "US-NY": "New York",
  "CA-NL": "Newfoundland and Labrador",
  "US-NC": "North Carolina",
  "US-ND": "North Dakota",
  "CA-NT": "Northwest Territories",
  "CA-NS": "Nova Scotia",
  "CA-NU": "Nunavut",
  "US-OH": "Ohio",
  "US-OK": "Oklahoma",
  "CA-ON": "Ontario",
  "US-OR": "Oregon",
  "US-PA": "Pennsylvania",
  "CA-PE": "Prince Edward Island",
  "CA-QC": "Quebec",
  "US-RI": "Rhode Island",
  "CA-SK": "Saskatchewan",
  "US-SC": "South Carolina",
  "US-SD": "South Dakota",
  "US-TN": "Tennessee",
  "US-TX": "Texas",
  "US-UT": "Utah",
  "US-VT": "Vermont",
  "US-VA": "Virginia",
  "US-WA": "Washington",
  "US-WV": "West Virginia",
  "US-WI": "Wisconsin",
  "US-WY": "Wyoming",
  "CA-YT": "Yukon",
};

export const PROVINCE_STATE_LIST = Object.keys(
  PROVINCE_STATE_VALUE_TO_LABEL_MAP,
).map((value) => ({
  value,
  label: PROVINCE_STATE_VALUE_TO_LABEL_MAP[value],
}));

type ShipmentServiceValueToLabelMap = Record<string, string>;

export const SHIPMENT_SERVICE_VALUE_TO_LABEL_MAP: ShipmentServiceValueToLabelMap =
  {
    TRUCK_LOAD: "Full Load (FTL/FCL)",
    LESS_THAN_TRUCK_LOAD: "Partial Load (LTL/LCL)",
  };

export const SHIPMENT_SERVICE_OPTIONS = Object.keys(
  SHIPMENT_SERVICE_VALUE_TO_LABEL_MAP,
).map((value) => ({
  value,
  label: SHIPMENT_SERVICE_VALUE_TO_LABEL_MAP[value],
}));

type EquipmentValueToLabelMap = Record<string, string>;

export const EQUIPMENT_VALUE_TO_LABEL_MAP: EquipmentValueToLabelMap = {
  DRY_VAN: "Dry Van",
  REEFER: "Reefer",
  FLAT_DECK: "Flat Deck",
  STEP_DECK_DROP_DECK: "Step Deck/Drop Deck",
  AUTO_HAULER_CAR_CARRIER: "Auto-Hauler/Car-Carrier",
  BOTTOM_SIDE_DUMP: "Bottom/Side Dump",
  CONESTOGA_ROLL_TITE: "Conestoga/Roll-Tite",
  DOUBLE_DROP_LOWBOY: "Double Drop/Lowboy",
  DRY_BULK_HOPPER: "Dry Bulk (Hopper)",
  DRY_BULK_SUPER_B_HOPPER: "Dry Bulk Super-B (Hopper)",
  DUMP_BODY_TRANSFER_BOX: "Dump Body/Transfer Box",
  DUMP_TRUCK: "Dump Truck",
  HEAVY_HAUL_OVER_DIMENSIONAL: "Heavy Haul/Over dimensional",
  HOTSHOT: "Hotshot",
  INTERMODAL_CHASSIS: "Intermodal/Chassis",
  LIQUID_BULK_TANKER: "Liquid Bulk (Tanker)",
  LIQUID_BULK_SUPER_B_TANKER: "Liquid Bulk Super-B (Tanker)",
  LIVE_BOTTOM_TRAILER: "Live Bottom Trailer",
  LIVESTOCK: "Livestock",
  LOGGING: "Logging",
  OTHER: "Other",
  POLE_TRAILER: "Pole Trailer",
  RGN: "RGN (Removable Gooseneck)",
  SIDE_KIT: "Side Kit",
  SPRINTER_VAN: "Sprinter Van",
  STRAIGHT_TRUCK: "Straight Truck",
  SUPER_B: "Super-B",
  TROMBONE_TRAILER: "Trombone Trailer",
  POWER_ONLY: "Power only",
  OCEAN_CONTAINER_40_FT: "40' Ocean Container",
  OCEAN_CONTAINER_20_FT: "20' Ocean Container",
  TILT_DECK: "Tilt Deck",
  CRANE_OR_MOBILE_CRANE: "Crane/Mobile Crane",
  HEATED_TRAILER: "Heated Trailer",
  AIR_FREIGHT: "Air Freight",
  ROLL_OFF_BIN: "Roll Off Bin",
};

export const MAX_VISIBLE_EQUIPMENT_TYPES = 12;

type ServiceAreadToLabelMap = Record<string, string>;
export const SERVICE_AREAS_TO_LABEL_MAP: ServiceAreadToLabelMap = {
  INTRA_CANADA: "Intra Canada",
  INTRA_USA: "Intra USA",
  CROSS_BORDER: "Cross Border (Canada/USA)",
  MEXICO: "Mexico",
  INTERNATIONAL: "International (Ocean)",
};

export const DEFAULT_EQUIPMENT_TYPE_OPTIONS = Object.keys(
  EQUIPMENT_VALUE_TO_LABEL_MAP,
).map((value) => ({
  value,
  label: EQUIPMENT_VALUE_TO_LABEL_MAP[value],
}));

export const SHIPMENT_TYPE_VALUE_TO_LABEL_MAP: Record<string, string> = {
  SINGLE: "Single",
  MULTI_SHIPMENT_PROJECT: "Multi-Shipment Project",
  QUOTES_ONLY: "Quotes Only Shipment",
};

export const FEATUREBASE_APP_ID = "69457fc82856aebcdc086c1e";
export const DEVELOPER_DOCS_URL = "https://developers.evotrux.com";
export const SHIPMENT_TRACKING_LINK_BLOG_URL =
  "https://www.evotrux.com/blog/tracking-links-on-evotrux";
export const WHAT_ARE_MUTLI_SHIPMENT_PROJECTS_BLOG_URL =
  "https://evotrux.featurebase.app/help/articles/2634119-different-shipment-types#a383e4e6z46";
export const QUOTING_MUTLI_SHIPMENT_PROJECTS_BLOG_URL =
  "https://evotrux.featurebase.app/en/help/articles/0018775-quoting-multi-shipment-projects";
export const MUTLI_SHIPMENT_PROJECTS_BILLING_FOR_CARRIERS_BLOG_URL =
  "https://evotrux.featurebase.app/en/help/articles/1269184-how-non-partner-multi-shipment-project-bookings-are-billed-for";
export const CUSTOMER_AUTOMATED_TRACKING_BLOG_URL =
  "https://evotrux.featurebase.app/en/help/articles/3440719-provide-your-customers-with-automated-shipment-tracking";
export const SHARE_LIVE_SHIPMENT_TRACKING_BLOG_URL =
  "https://evotrux.featurebase.app/en/help/articles/7793737-how-to-share-live-shipment-tracking-with-your-customers";
export const TOTAL_FREIGHT_COSTS_CARRIER_BLOG_URL =
  "https://evotrux.featurebase.app/en/help/articles/7724520-how-to-submit-total-freight-costs-after-delivery";
export const TOTAL_FREIGHT_COSTS_SHIPPER_BLOG_URL =
  "https://evotrux.featurebase.app/en/help/articles/1962966-how-to-review-and-approve-total-freight-costs";
export const QUOTES_ONLY_SHIPMENT_BLOG_URL =
  "http://www.evotrux.com/blog/quote-only-shipment-postings";
export const QUOTE_COMPETITIVENESS_SCORE_BLOG_URL =
  "https://evotrux.featurebase.app/en/help/articles/8447447-quote-competitiveness-score";

export const LIMITED_BOOKING_CARRIERS_SUBSCRIPTION_PLANS: Set<SubscriptionPlan> =
  new Set<SubscriptionPlan>([
    "CARRIER_ESSENTIALS",
    "CARRIER_BRONZE",
    "CARRIER_SILVER",
    "CARRIER_GOLD",
    // 'CARRIER_ENTERPRISE_2', unlimited
  ]);

export const FREE_CARRIER_SUBSCRIPTION_PLANS: Set<SubscriptionPlan> =
  new Set<SubscriptionPlan>([
    "CARRIER_ESSENTIALS",
    "CARRIER_ESSENTIALS_V2",
    "CARRIER_PAY_PER_BOOKING",
  ]);

// Plans billed outside Stripe. No StripeCustomer exists for them, so a billing
// portal session cannot be created and anything that would send the user there
// has to offer something else instead.
export const PLANS_WITHOUT_STRIPE_CUSTOMER: Set<SubscriptionPlan> =
  new Set<SubscriptionPlan>([
    "CARRIER_ESSENTIALS",
    "CARRIER_ESSENTIALS_V2",
    "SHIPPER_TRIAL",
    "SHIPPER_PREMIUM_OFF_STRIPE",
  ]);

// File upload constants for react-dropzone accept prop
export const FILE_UPLOAD_ACCEPT = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/plain": [".txt"],
  "image/*": [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".svg",
    ".webp",
    ".tiff",
    ".tif",
  ],
};

// Matches the WorkPhoneNumber.extension column (varchar(10)) on the backend.
export const WORK_PHONE_EXTENSION_MAX_LENGTH = 10;

// Simple array for validation
export const ALLOWED_FILE_EXTENSIONS = [
  // Documents
  "docx",
  "doc",
  "txt",
  "pdf",
  // Images
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "svg",
  "webp",
  "tiff",
  "tif",
];

export const MAX_FILE_SIZE_MB = 100;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Subscription plan color mapping
export const SUBSCRIPTION_PLAN_COLORS: Record<string, string> = {
  essentials: "#000000", // Standard black
  bronze: "#CD7F32", // Bronze color
  silver: "#C0C0C0", // Silver color
  gold: "#FFD700", // Gold color
};

export const AppPage = {
  DASHBOARD: "DASHBOARD",
  SHIPMENT_DETAIL: "SHIPMENT_DETAIL",
} as const;
export type AppPage = (typeof AppPage)[keyof typeof AppPage];

export const ClientAppEvent = {
  USER_LOGGED_IN: "USER_LOGGED_IN",
  USER_SIGNED_OUT: "USER_SIGNED_OUT",
  SHIPMENT_EXPORTED_TO_PDF: "SHIPMENT_EXPORTED_TO_PDF",
  REVEAL_BOOKED_PRICE_CLICKED: "REVEAL_BOOKED_PRICE_CLICKED",
  SHIPMENT_POSTED: "SHIPMENT_POSTED",
  SHIPMENT_POSTING_FAILED: "SHIPMENT_POSTING_FAILED",
  SHIPMENT_POSTING_BLOCKED_BY_VALIDATION:
    "SHIPMENT_POSTING_BLOCKED_BY_VALIDATION",
  SHIPMENT_FILES_UPLOADED: "SHIPMENT_FILES_UPLOADED",
  SHIPMENT_DETAIL_FILE_DOWNLOADED: "SHIPMENT_DETAIL_FILE_DOWNLOADED",
  SHIPMENT_TEMPLATE_CREATED_FROM_SHIPMENT:
    "SHIPMENT_TEMPLATE_CREATED_FROM_SHIPMENT",
  SHIPMENT_TEMPLATE_CREATED_FROM_FORM: "SHIPMENT_TEMPLATE_CREATED_FROM_FORM",
  SHIPMENT_TEMPLATE_APPLIED: "SHIPMENT_TEMPLATE_APPLIED",
  SHIPMENT_COPY_INITIATED: "SHIPMENT_COPY_INITIATED",
  OFF_PLATFORM_BOOKING_MODAL_OPENED: "OFF_PLATFORM_BOOKING_MODAL_OPENED",
  OFF_PLATFORM_BOOKING_SUBMITTED: "OFF_PLATFORM_BOOKING_SUBMITTED",
  CANCEL_SHIPMENT_MODAL_OPENED: "CANCEL_SHIPMENT_MODAL_OPENED",
  SHIPMENT_CANCELLED: "SHIPMENT_CANCELLED",
  SHIPMENT_CANCELLED_WITH_PENDING_OFF_PLATFORM_BOOKING:
    "SHIPMENT_CANCELLED_WITH_PENDING_OFF_PLATFORM_BOOKING",
  SHIPMENT_COPY_MODAL_OPENED: "SHIPMENT_COPY_MODAL_OPENED",
  DATA_PAGE_DEMO_VIEWED: "DATA_PAGE_DEMO_VIEWED",
  SHIPMENT_TRACKING_DATA_PAGE_DEMO_VIEWED:
    "SHIPMENT_TRACKING_DATA_PAGE_DEMO_VIEWED",
  VOLUNTELL_ACTIVATION_LINK_PAGE_SHOWN: "VOLUNTELL_ACTIVATION_LINK_PAGE_SHOWN",
  VOLUNTELL_ACCOUNT_ACTIVATION_LINK_HIT:
    "VOLUNTELL_ACCOUNT_ACTIVATION_LINK_HIT",
  VOLUNTELL_ACCOUNT_ACTIVATION: "VOLUNTELL_ACCOUNT_ACTIVATION",
  COMPANY_MEMBER_ACCOUNT_ACTIVATION: "COMPANY_MEMBER_ACCOUNT_ACTIVATION",
  ACCOUNT_ACTIVATION_PAGE_VIEWED: "ACCOUNT_ACTIVATION_PAGE_VIEWED",
  VOLUNTELL_USER_PASSWORD_RESET: "VOLUNTELL_USER_PASSWORD_RESET",
  USER_PASSWORD_RESET: "USER_PASSWORD_RESET",
  INSTANT_CHAT_MESSAGE_SENT: "INSTANT_CHAT_MESSAGE_SENT",
  INSTANT_CHAT_MESSAGE_RECEIVED: "INSTANT_CHAT_MESSAGE_RECEIVED",
  INSTANT_CHAT_FILE_ATTACHEMENT_SENT: "INSTANT_CHAT_FILE_ATTACHEMENT_SENT",
  INSTANT_CHAT_PUBLISH_TOKEN_FETCHED: "INSTANT_CHAT_PUBLISH_TOKEN_FETCHED",
  CARRIER_COMPANY_PROFILE_UPDATED: "CARRIER_COMPANY_PROFILE_UPDATED",
  CARRIER_COMPANY_LOGO_UPDATED: "CARRIER_COMPANY_LOGO_UPDATED",
  CARRIER_COMPANY_LOGO_REMOVED: "CARRIER_COMPANY_LOGO_REMOVED",
  CARRIER_PROFILE_BANNER_VIEWED: "carrier_profile_banner_viewed",
  CARRIER_PROFILE_BANNER_CLICKED: "carrier_profile_banner_clicked",
  PPB_PAYMENT_METHOD_BANNER_VIEWED: "ppb_payment_method_banner_viewed",
  PPB_PAYMENT_METHOD_BANNER_CLICKED: "ppb_payment_method_banner_clicked",
  CARRIER_PROFILE_COMPLETION_ALERT_CTA_CLICKED:
    "carrier_profile_completion_alert_cta_clicked",
  CARRIER_PROFILE_COMPLETION_ALERT_ADMIN_CONTACT_CLICKED:
    "carrier_profile_completion_alert_admin_contact_clicked",
  CUSTOMER_REQUIREMENTS_AUTO_POPULATED: "CUSTOMER_REQUIREMENTS_AUTO_POPULATED",
  QUOTE_SUBMITTED: "QUOTE_SUBMITTED",
  CARRIER_PUBLIC_SHIPMENTS_SUBSCRIBE_CLICKED:
    "CARRIER_PUBLIC_SHIPMENTS_SUBSCRIBE_CLICKED",
  CARRIER_SHIPPER_NAME_SUBSCRIBE_CLICKED:
    "CARRIER_SHIPPER_NAME_SUBSCRIBE_CLICKED",
  CARRIER_PUBLIC_SHIPMENTS_LEARN_MORE_CLICKED:
    "CARRIER_PUBLIC_SHIPMENTS_LEARN_MORE_CLICKED",
  CARRIER_QUOTE_REJECTED_NO_INSURANCE: "CARRIER_QUOTE_REJECTED_NO_INSURANCE",
  CARRIER_QUOTE_REJECTED_EXPIRED_INSURANCE:
    "CARRIER_QUOTE_REJECTED_EXPIRED_INSURANCE",
  CARRIER_NETWORK_INVITE_REJECTED_INSUFFICIENT_SLOTS:
    "CARRIER_NETWORK_INVITE_REJECTED_INSUFFICIENT_SLOTS",
  SHIPPER_UPGRADE_NOW_BUTTON_CLICKED: "SHIPPER_UPGRADE_NOW_BUTTON_CLICKED",
  CARRIER_BOOKING_SHARE_BUTTON_CLICKED: "CARRIER_BOOKING_SHARE_BUTTON_CLICKED",
  CARRIER_BOOKING_SHARED_WITH_TEAMMATES:
    "CARRIER_BOOKING_SHARED_WITH_TEAMMATES",
  PROJECT_BOOKED_NOW: "PROJECT_BOOKED_NOW",
  MULTI_SHIPMENT_PROJECT_LOADS_ASSIGNED:
    "MULTI_SHIPMENT_PROJECT_LOADS_ASSIGNED",
  QUOTE_CANCELLED: "QUOTE_CANCELLED",
  QUOTE_ACCEPTED: "QUOTE_ACCEPTED",
  QUOTE_DECLINED: "QUOTE_DECLINED",
  QUOTE_COUNTERED: "QUOTE_COUNTERED",
  QUOTE_QUESTION_ASKED: "QUOTE_QUESTION_ASKED",
  // The in-app half of the quote answer series. Answers written from inbound
  // email are captured server-side under the same name by
  // ProductAnalyticsEventHandlingService, split on the `source` property
  // (docs/engineering/design/qa_product_analytics_listener_design.md §8).
  QUOTE_QUESTION_ANSWERED: "QUOTE_QUESTION_ANSWERED",
  QUOTE_DECLINED_WITH_FEEDBACK: "QUOTE_DECLINED_WITH_FEEDBACK",
  SHIPMENT_BOOKING_CANCELLED: "SHIPMENT_BOOKING_CANCELLED",
  MULTI_SHIPMENT_PROJECT_FINALIZED: "MULTI_SHIPMENT_PROJECT_FINALIZED",
  COMPANY_ADDRESS_UPDATED: "COMPANY_ADDRESS_UPDATED",
  COMPANY_REVIEW_PROMPT_SHOWN: "COMPANY_REVIEW_PROMPT_SHOWN",
  COMPANY_REVIEW_PROMPT_CLICKED: "COMPANY_REVIEW_PROMPT_CLICKED",
  COMPANY_REVIEW_SUBMITTED: "COMPANY_REVIEW_SUBMITTED",
  SHIPMENT_SHARED_WITH_TEAMMATES: "SHIPMENT_SHARED_WITH_TEAMMATES",
  PUBLIC_SHIPMENT_SHARE_MODAL_OPENED: "PUBLIC_SHIPMENT_SHARE_MODAL_OPENED",
  PUBLIC_SHIPMENT_LINK_COPIED: "PUBLIC_SHIPMENT_LINK_COPIED",
  PUBLIC_SHIPMENT_LINK_OPENED: "PUBLIC_SHIPMENT_LINK_OPENED",
  PUBLIC_SHIPMENT_SHARING_HELP_OPENED: "PUBLIC_SHIPMENT_SHARING_HELP_OPENED",
  SHIPMENT_PARTNERS_ADDED: "SHIPMENT_PARTNERS_ADDED",
  SHIPMENT_CUSTOMERS_ADDED: "SHIPMENT_CUSTOMERS_ADDED",
  SHIPMENT_CUSTOM_NAME_UPDATED: "SHIPMENT_CUSTOM_NAME_UPDATED",
  SHIPMENT_EMAIL_NOTIFICATIONS_UPDATED: "SHIPMENT_EMAIL_NOTIFICATIONS_UPDATED",
  SHIPMENT_MARKED_AS_DELIVERED: "SHIPMENT_MARKED_AS_DELIVERED",
  SHIPMENT_DELIVERY_FILE_DOWNLOADED: "SHIPMENT_DELIVERY_FILE_DOWNLOADED",
  SHIPMENT_MARKED_AS_PICKED_UP: "SHIPMENT_MARKED_AS_PICKED_UP",
  ROUTE_POINT_ETA_ADDED: "ROUTE_POINT_ETA_ADDED",
  SHIPMENT_TRACKING_LINK_ADDED: "TRACKING_LINK_ADDED",
  TRACKING_LINK_DELETED: "TRACKING_LINK_DELETED",
  CARRIER_BLOCKED: "CARRIER_BLOCKED",
  NETWORK_GROUP_CREATED: "NETWORK_GROUP_CREATED",
  NETWORK_GROUP_NAME_UPDATED: "NETWORK_GROUP_NAME_UPDATED",
  PARTNERS_ADDED_TO_NETWORK_GROUP: "PARTNERS_ADDED_TO_NETWORK_GROUP",
  PARTNER_REMOVED_FROM_NETWORK_GROUP: "PARTNER_REMOVED_FROM_NETWORK_GROUP",
  SUPPLIER_INVITE_SENT_INTERNAL: "SUPPLIER_INVITE_SENT_INTERNAL",
  SUPPLIER_INVITE_SENT_EXTERNAL: "SUPPLIER_INVITE_SENT_EXTERNAL",
  SUPPLIER_NETWORK_INVITE_RESPONDED: "SUPPLIER_NETWORK_INVITE_RESPONDED",
  CUSTOMER_INVITE_SENT_INTERNAL: "CUSTOMER_INVITE_SENT_INTERNAL",
  CUSTOMER_ADDED_EXTERNAL: "CUSTOMER_ADDED_EXTERNAL",
  CUSTOMER_NETWORK_INVITE_RESPONDED: "CUSTOMER_NETWORK_INVITE_RESPONDED",
  CHAT_NOTIFICATIONS_TOGGLED: "CHAT_NOTIFICATIONS_TOGGLED",
  TEAM_MEMBER_ADDED: "TEAM_MEMBER_ADDED",
  CARRIER_COMPANY_CREATED: "CARRIER_COMPANY_CREATED",
  SHIPPER_COMPANY_CREATED: "SHIPPER_COMPANY_CREATED",
  SHIPPER_SCHEDULE_ONBOARDING_CLICKED: "SHIPPER_SCHEDULE_ONBOARDING_CLICKED",
  JOIN_COMPANY_REQUEST_SENT: "JOIN_COMPANY_REQUEST_SENT",
  SHIPPER_PREMIUM_INFO_REQUESTED: "SHIPPER_PREMIUM_INFO_REQUESTED",
  CARRIER_ENTERPRISE_INFO_REQUESTED: "CARRIER_ENTERPRISE_INFO_REQUESTED",
  PAY_PER_BOOKING_SUBSCRIPTION_CREATED: "PAY_PER_BOOKING_SUBSCRIPTION_CREATED",
  SHIPPER_PENDING_REQUEST_DELETED: "SHIPPER_PENDING_REQUEST_DELETED",
  SHIPPER_NETWORK_CARRIER_REMOVED: "SHIPPER_NETWORK_CARRIER_REMOVED",
  CARRIER_SHIPMENT_TAB_CLICKED: "CARRIER_SHIPMENT_TAB_CLICKED",
  CARRIER_PARTNER_RECOVERY_BUTTON_CLICKED:
    "CARRIER_PARTNER_RECOVERY_BUTTON_CLICKED",
  CARRIER_LANE_PREFERENCES_UPDATED: "CARRIER_LANE_PREFERENCES_UPDATED",
  CARRIER_LANE_TEMPLATE_APPLIED: "CARRIER_LANE_TEMPLATE_APPLIED",
  CARRIER_FILTER_BUTTON_CLICKED: "CARRIER_FILTER_BUTTON_CLICKED",
  CARRIER_CREATE_LANES_BUTTON_CLICKED: "CARRIER_CREATE_LANES_BUTTON_CLICKED",
  CARRIER_LANE_CREATED_WITH_EXPIRY: "CARRIER_LANE_CREATED_WITH_EXPIRY",
  CARRIER_LANE_EDITED: "CARRIER_LANE_EDITED",
  CARRIER_DASHBOARD_SHIPMENT_FILTER_SELECTED:
    "CARRIER_DASHBOARD_SHIPMENT_FILTER_SELECTED",
  CARRIER_MY_QUOTES_FILTER_SELECTED: "CARRIER_MY_QUOTES_FILTER_SELECTED",
  CARRIER_MY_QUOTES_MORE_FILTERS_TOGGLED:
    "CARRIER_MY_QUOTES_MORE_FILTERS_TOGGLED",
  CARRIER_SHIPMENT_DETAIL_SIDEBAR_VIEW_CHANGED:
    "CARRIER_SHIPMENT_DETAIL_SIDEBAR_VIEW_CHANGED",
  CARRIER_SHIPMENT_DETAIL_QUOTE_FILTER_BUTTON_CLICKED:
    "CARRIER_SHIPMENT_DETAIL_QUOTE_FILTER_BUTTON_CLICKED",
  SHIPMENT_PRIVATE_NOTE_CREATED: "SHIPMENT_PRIVATE_NOTE_CREATED",
  SHIPMENT_PRIVATE_NOTE_DELETED: "SHIPMENT_PRIVATE_NOTE_DELETED",
  SHIPMENT_MICRO_REVIEW_SUBMITTED: "SHIPMENT_MICRO_REVIEW_SUBMITTED",
  CARRIER_SHIPMENT_NOTIFICATION_PREFERENCE_UPDATED:
    "CARRIER_SHIPMENT_NOTIFICATION_PREFERENCE_UPDATED",
  COMMAND_LINK_LANDING_WITHOUT_TOKEN: "COMMAND_LINK_LANDING_WITHOUT_TOKEN",
  EMAIL_UNSUBSCRIBE_LINK_PREVIEWED: "EMAIL_UNSUBSCRIBE_LINK_PREVIEWED",
  SUPPLIER_SHIPMENT_CUSTOMER_NOTIFICATION_PREFERENCE_UPDATED:
    "SUPPLIER_SHIPMENT_CUSTOMER_NOTIFICATION_PREFERENCE_UPDATED",
  SHIPMENT_QUESTION_CREATED: "SHIPMENT_QUESTION_CREATED",
  SHIPMENT_QUESTION_ANSWERED: "SHIPMENT_QUESTION_ANSWERED",
  TFC_SUBMITTED: "TFC_SUBMITTED",
  TFC_AMENDED: "TFC_AMENDED",
  TFC_RESUBMITTED: "TFC_RESUBMITTED",
  TFC_APPROVED: "TFC_APPROVED",
  TFC_REJECTED: "TFC_REJECTED",
  TFC_UNDO_AND_ACCEPTED: "TFC_UNDO_AND_ACCEPTED",
  TFC_REOPENED: "TFC_REOPENED",
  TFC_HISTORY_OPENED: "TFC_HISTORY_OPENED",
  TFC_SHARED: "TFC_SHARED",
  CHAT_INBOX_UNREAD_RECEIVED: "CHAT_INBOX_UNREAD_RECEIVED",
  CHAT_INBOX_ROW_OPENED: "CHAT_INBOX_ROW_OPENED",
  SHIPPER_BOOKING_STATS_VIEWED: "SHIPPER_BOOKING_STATS_VIEWED",
  SHIPPER_BOOKING_STATS_TEASER_CLICKED: "SHIPPER_BOOKING_STATS_TEASER_CLICKED",
  CARRIER_UPGRADE_CTA_CLICKED: "CARRIER_UPGRADE_CTA_CLICKED",
  CARRIER_QUOTE_LAST_VIEWED_INDICATOR_VIEWED:
    "CARRIER_QUOTE_LAST_VIEWED_INDICATOR_VIEWED",
  SUGGESTED_ROUTE_DELETED: "SUGGESTED_ROUTE_DELETED",
  SHIPPER_EMAIL_NOTIFICATION_PREFERENCE_TOGGLED:
    "SHIPPER_EMAIL_NOTIFICATION_PREFERENCE_TOGGLED",
  SHIPMENT_LIVE_UPDATE_BANNER_SHOWN: "SHIPMENT_LIVE_UPDATE_BANNER_SHOWN",
  SHIPMENT_LIVE_UPDATE_REFRESH_ACCEPTED:
    "SHIPMENT_LIVE_UPDATE_REFRESH_ACCEPTED",
  SHIPMENT_LIVE_UPDATE_BANNER_DISMISSED:
    "SHIPMENT_LIVE_UPDATE_BANNER_DISMISSED",
  SIGNUP_ERROR: "SIGNUP_ERROR",
  SIGNUP_SUBMITTED: "SIGNUP_SUBMITTED",
  SIGNUP_SUCCEEDED: "SIGNUP_SUCCEEDED",
  LOGIN_ERROR: "LOGIN_ERROR",
  REPORT_MODAL_OPENED: "REPORT_MODAL_OPENED",
  REPORT_SUBMITTED: "REPORT_SUBMITTED",
  LAPSED_SUBSCRIPTION_BANNER_SHOWN: "LAPSED_SUBSCRIPTION_BANNER_SHOWN",
  LAPSED_SUBSCRIPTION_BANNER_CTA_CLICKED:
    "LAPSED_SUBSCRIPTION_BANNER_CTA_CLICKED",
  LAPSED_SUBSCRIPTION_BANNER_SECONDARY_CLICKED:
    "LAPSED_SUBSCRIPTION_BANNER_SECONDARY_CLICKED",
  LAPSED_SUBSCRIPTION_RECOVERED: "LAPSED_SUBSCRIPTION_RECOVERED",
  SHIPPER_SHIPMENT_SIDEBAR_TOGGLED: "SHIPPER_SHIPMENT_SIDEBAR_TOGGLED",
  ADD_CARRIER_TO_NETWORK_CLICKED: "ADD_CARRIER_TO_NETWORK_CLICKED",
  CARRIER_VIEWED_ANTI_SOLICITATION_AGREEMENT:
    "CARRIER_VIEWED_ANTI_SOLICITATION_AGREEMENT",
  CARRIER_ACCEPTED_ANTI_SOLICITATION_AGREEMENT:
    "CARRIER_ACCEPTED_ANTI_SOLICITATION_AGREEMENT",
  CREATE_SHIPMENT_PENDING_INVITE_SELECTED:
    "CREATE_SHIPMENT_PENDING_INVITE_SELECTED",
  CREATE_SHIPMENT_DIRECT_BOOKING_SELECTED:
    "CREATE_SHIPMENT_DIRECT_BOOKING_SELECTED",
  CREATE_SHIPMENT_DIRECT_BOOKING_ASSIGNEE_CHECKED:
    "CREATE_SHIPMENT_DIRECT_BOOKING_ASSIGNEE_CHECKED",
  EMBEDDED_DASHBOARD_VIEWED: "EMBEDDED_DASHBOARD_VIEWED",
  EMBEDDED_DASHBOARD_RENDERED: "EMBEDDED_DASHBOARD_RENDERED",
  EMBEDDED_DASHBOARD_LINK_OPENED: "EMBEDDED_DASHBOARD_LINK_OPENED",
  EMBEDDED_DASHBOARD_COLUMNS_CHANGED: "EMBEDDED_DASHBOARD_COLUMNS_CHANGED",
  CARRIER_SEARCH_BAR_CLICKED: "CARRIER_SEARCH_BAR_CLICKED",
  CARRIER_SEARCH_RESULT_SELECTED: "CARRIER_SEARCH_RESULT_SELECTED",
  CARRIER_PROFILE_VIEWED: "CARRIER_PROFILE_VIEWED",
  SHIPPER_BOOKED_PRICE_REVEAL_TOGGLED: "SHIPPER_BOOKED_PRICE_REVEAL_TOGGLED",
  CARRIER_QUOTE_COMPETITIVENESS_SCORE_PANEL_EXPANDED:
    "CARRIER_QUOTE_COMPETITIVENESS_SCORE_PANEL_EXPANDED",
  CARRIER_QUOTE_COMPETITIVENESS_SCORE_VIEWED:
    "CARRIER_QUOTE_COMPETITIVENESS_SCORE_VIEWED",
  CARRIER_QUOTE_COMPETITIVENESS_SCORE_LEARN_MORE_CLICKED:
    "CARRIER_QUOTE_COMPETITIVENESS_SCORE_LEARN_MORE_CLICKED",
  SHIPPER_QUOTE_COMPETITIVENESS_SCORE_PANEL_EXPANDED:
    "SHIPPER_QUOTE_COMPETITIVENESS_SCORE_PANEL_EXPANDED",
  SHIPPER_QUOTE_COMPETITIVENESS_SCORE_SORT_APPLIED:
    "SHIPPER_QUOTE_COMPETITIVENESS_SCORE_SORT_APPLIED",
  QUOTE_CONVERTED_PRICE_TOOLTIP_OPENED: "QUOTE_CONVERTED_PRICE_TOOLTIP_OPENED",
  // Shipper quote table actions (docs/engineering/design/shipper_quote_table_redesign_v2_design.md §7)
  SHIPPER_QUOTE_ACTIONS_MENU_OPENED: "SHIPPER_QUOTE_ACTIONS_MENU_OPENED",
  SHIPPER_QUOTE_INSURANCE_VIEWED: "SHIPPER_QUOTE_INSURANCE_VIEWED",
  SHIPPER_QUOTE_HISTORY_VIEWED: "SHIPPER_QUOTE_HISTORY_VIEWED",
  SHIPPER_QUOTE_BLOCK_CARRIER_CLICKED: "SHIPPER_QUOTE_BLOCK_CARRIER_CLICKED",
  SHIPPER_QUOTE_NOTE_EXPANDED: "SHIPPER_QUOTE_NOTE_EXPANDED",
  // Submit-quote command-link landing funnel: visit -> preview -> interaction ->
  // submit -> outcome -> app handoff
  // (docs/engineering/design/carrier_submit_quote_command_link_design.md §8).
  QUOTE_COMMAND_LINK_VISITED: "QUOTE_COMMAND_LINK_VISITED",
  QUOTE_COMMAND_LINK_PREVIEWED: "QUOTE_COMMAND_LINK_PREVIEWED",
  QUOTE_COMMAND_LINK_PREVIEW_FAILED: "QUOTE_COMMAND_LINK_PREVIEW_FAILED",
  QUOTE_COMMAND_LINK_BLOCKED_VIEW: "QUOTE_COMMAND_LINK_BLOCKED_VIEW",
  QUOTE_COMMAND_LINK_FORM_STARTED: "QUOTE_COMMAND_LINK_FORM_STARTED",
  QUOTE_COMMAND_LINK_REQUIREMENTS_APPLIED:
    "QUOTE_COMMAND_LINK_REQUIREMENTS_APPLIED",
  QUOTE_COMMAND_LINK_SUBMIT_CLICKED: "QUOTE_COMMAND_LINK_SUBMIT_CLICKED",
  QUOTE_COMMAND_LINK_SUBMIT_FAILED: "QUOTE_COMMAND_LINK_SUBMIT_FAILED",
  QUOTE_COMMAND_LINK_QA_OPENED: "QUOTE_COMMAND_LINK_QA_OPENED",
  QUOTE_COMMAND_LINK_MANAGE_QUOTE_CLICKED:
    "QUOTE_COMMAND_LINK_MANAGE_QUOTE_CLICKED",
  QUOTE_COMMAND_LINK_TEAMMATE_QUOTES_CLICKED:
    "QUOTE_COMMAND_LINK_TEAMMATE_QUOTES_CLICKED",
  QUOTE_COMMAND_LINK_BOOK_NOW_CLICKED: "QUOTE_COMMAND_LINK_BOOK_NOW_CLICKED",
  NO_CAPACITY_CLICKED: "NO_CAPACITY_CLICKED",
  NO_CAPACITY_UNDONE: "NO_CAPACITY_UNDONE",
  MATCH_TABLE_OPENED: "MATCH_TABLE_OPENED",
  TELL_MORE_CARRIERS_OPENED: "TELL_MORE_CARRIERS_OPENED",
  TELL_MORE_CARRIERS_SHIPMENT_INFO_COPIED:
    "TELL_MORE_CARRIERS_SHIPMENT_INFO_COPIED",
  NOTIFICATION_FILTER_CHANGED: "NOTIFICATION_FILTER_CHANGED",
} as const;
export type ClientAppEvent =
  (typeof ClientAppEvent)[keyof typeof ClientAppEvent];

// Identifies which surface sent a viewer to a carrier profile. Carried as the
// `profile_source` property on CARRIER_PROFILE_VIEWED so profile traffic splits
// into a single PostHog breakdown. Entry points that link to a profile without
// a source report as DIRECT, which also covers arrivals the app does not link
// at all: a bookmark, a typed URL, or a link followed from outside the app.
export const CarrierProfileSource = {
  HEADER_SEARCH: "HEADER_SEARCH",
  SHIPMENT_QUOTE_TABLE_ROW: "SHIPMENT_QUOTE_TABLE_ROW",
  SHIPMENT_PROGRESS_DETAIL: "SHIPMENT_PROGRESS_DETAIL",
  DIRECT: "DIRECT",
} as const;
export type CarrierProfileSource =
  (typeof CarrierProfileSource)[keyof typeof CarrierProfileSource];

export const CARRIER_PROFILE_SOURCE_QUERY_PARAM = "source";

// Identifies which upgrade component sent a carrier to the upgrade page.
// Carried as the `upgrade_source` property on CARRIER_UPGRADE_CTA_CLICKED so
// per-component upgrade traffic is a single PostHog breakdown.
export const CarrierUpgradeSource = {
  AVG_BOOKING_TEASER: "avg_booking_teaser",
  LAST_VIEWED_QUOTE: "last_viewed_quote",
  HIDDEN_SHIPPER_NAME: "hidden_shipper_name",
  BOOKED_PRICE_REVEAL: "booked_price_reveal",
  PUBLIC_SHIPMENTS_OVERLAY: "public_shipments_overlay",
  FREE_CARRIER_BANNER: "free_carrier_banner",
  PAY_PER_BOOKING_BANNER: "pay_per_booking_banner",
  ESSENTIALS_BOOKINGS_REMAINING_BANNER: "essentials_bookings_remaining_banner",
  QUOTE_COUNT_CHIP: "quote_count_chip",
  QUOTE_STATUS_BADGE: "quote_status_badge",
  NO_BOOKINGS_REMAINING_MODAL: "no_bookings_remaining_modal",
  QUOTE_INTERACTION: "quote_interaction",
  QUOTE_SCORE: "quote_score",
} as const;
export type CarrierUpgradeSource =
  (typeof CarrierUpgradeSource)[keyof typeof CarrierUpgradeSource];

// Identifies which surface a shipper blocked a carrier from. Carried as the
// `block_source` property on CARRIER_BLOCKED so per-surface blocks are a single
// PostHog breakdown.
export const CarrierBlockedSource = {
  SHIPMENT_PAGE: "shipment_page",
  SHIPMENT_QUESTION: "shipment_question",
  SHIPMENT_QUOTE_TABLE_ROW: "shipment_quote_table_row",
  CARRIER_PROFILE_TEAM: "carrier_profile_team",
} as const;
export type CarrierBlockedSource =
  (typeof CarrierBlockedSource)[keyof typeof CarrierBlockedSource];

// Every member must match CommandLinkName in
// application/commandLink/constants/__init__.py — the value is the URL segment
// the backend builds its emailed links from.
export const CommandLinkName = {
  ACTIVATE_VOLUNTELL_USER: "activate-voluntell-user",
  SUBMIT_QUOTE: "submit-quote",
  UPDATE_CUSTOMER_NOTIFICATION_PREFERENCE:
    "update-customer-notification-preference",
} as const;
export type CommandLinkName =
  (typeof CommandLinkName)[keyof typeof CommandLinkName];

// The query param every command-link landing reads its signed token from.
export const COMMAND_LINK_TOKEN_QUERY_PARAM_KEY = "t";

// The same token's name on the command-link API's own preview call, which is a
// different key from the one the emailed landing URL uses. Both carry a bearer
// credential, so both are stripped from anything sent to analytics.
export const COMMAND_LINK_API_TOKEN_QUERY_PARAM_KEY = "token";

export const SupplierShipmentCustomerNotificationPreferenceUpdateSource = {
  IN_APP: "IN_APP",
  EMAIL_COMMAND_LINK: "EMAIL_COMMAND_LINK",
} as const;
export type SupplierShipmentCustomerNotificationPreferenceUpdateSource =
  (typeof SupplierShipmentCustomerNotificationPreferenceUpdateSource)[keyof typeof SupplierShipmentCustomerNotificationPreferenceUpdateSource];

export const APIErrorCode = {
  NO_CARRIER_INSURANCE: "NO_CARRIER_INSURANCE",
  EXPIRED_CARRIER_INSURANCE: "EXPIRED_CARRIER_INSURANCE",
  INSUFFICIENT_NETWORK_SLOTS: "INSUFFICIENT_NETWORK_SLOTS",
  MISSING_POSTING_PERMISSION: "MISSING_POSTING_PERMISSION",
  DIRECT_BOOKING_ASSIGNEE_NOT_FOUND: "DIRECT_BOOKING_ASSIGNEE_NOT_FOUND",
  DIRECT_BOOKING_ASSIGNEE_NOT_CARRIER: "DIRECT_BOOKING_ASSIGNEE_NOT_CARRIER",
  DIRECT_BOOKING_ASSIGNEE_WITHOUT_COMPANY:
    "DIRECT_BOOKING_ASSIGNEE_WITHOUT_COMPANY",
  DIRECT_BOOKING_ASSIGNEE_DISABLED: "DIRECT_BOOKING_ASSIGNEE_DISABLED",
  DIRECT_BOOKING_ASSIGNEE_BLOCKED: "DIRECT_BOOKING_ASSIGNEE_BLOCKED",
  DIRECT_BOOKING_INVALID_SHIPMENT: "DIRECT_BOOKING_INVALID_SHIPMENT",
} as const;
export type APIErrorCode = (typeof APIErrorCode)[keyof typeof APIErrorCode];

export const EVOTRUX_URL_HASH = {
  CARRIER_EMERGENCY_CONTACT: "#emergency-contact",
  CARRIER_LANE_PREFERENCES: "#lane-preferences",
  LIVE_CHAT: "#live-chat",
};

export const EXPAND_LANES_QUERY_PARAM = "expandLanes";

// HowdyGo publication ids, taken from the share link the recordings are
// delivered in (app.howdygo.com/share/<id>); the embed builds its iframe URL
// from the same id. Re-recording a walkthrough mints a new id, so these change
// whenever the demo is refreshed — confirm a replacement plays the walkthrough
// named below before swapping it in, since a crossed pair renders each page the
// other's demo and nothing in the build can catch it.
export const DATA_PRODUCT_DEMO_HOWDYGO_PUBLICATION_ID =
  "eca241cc-1d33-4736-bf40-7446e84ddb60"; // analytics dashboard walkthrough
export const TRACKING_SHEET_HOWDYGO_PUBLICATION_ID =
  "0791c82f-a955-499c-8d87-c9cb271bf9f3"; // tracking sheet walkthrough

// UTM tracking for analytics. Every value is kebab-cased so the whole UTM
// vocabulary reads the same way in reports. Sources distinguish traffic origins:
// - 'ses-email-notification': Email notifications sent via AWS SES (backend)
// - 'in-app-notification': In-app notification clicks (frontend)
// - 'command-link': Deep links from an emailed command-link landing page into the
//   app (docs/engineering/design/carrier_submit_quote_command_link_design.md §8)
// Medium and campaign are only set on command-link deep links today.
export const UTM_SOURCE_QUERY_PARAM_KEY = "utm_source";
export const UTM_MEDIUM_QUERY_PARAM_KEY = "utm_medium";
export const UTM_CAMPAIGN_QUERY_PARAM_KEY = "utm_campaign";
export const UTM_SOURCE_IN_APP_NOTIFICATION = "in-app-notification";
export const UTM_SOURCE_COMMAND_LINK = "command-link";
export const UTM_MEDIUM_EMAIL = "email";

export const CommandLinkUtmCampaign = {
  SUBMIT_QUOTE_QA: "submit-quote-qa",
  SUBMIT_QUOTE_MANAGE: "submit-quote-manage",
  // Separate from SUBMIT_QUOTE_MANAGE: every created quote ends on the
  // post-submission handoff, so sharing a campaign hid it inside the manage figure.
  SUBMIT_QUOTE_SUBMITTED: "submit-quote-submitted",
  SUBMIT_QUOTE_TEAMMATES: "submit-quote-teammates",
  SUBMIT_QUOTE_BOOK_NOW: "submit-quote-book-now",
  SUBMIT_QUOTE_LOGGED_IN_REDIRECT: "submit-quote-logged-in-redirect",
} as const;
export type CommandLinkUtmCampaign =
  (typeof CommandLinkUtmCampaign)[keyof typeof CommandLinkUtmCampaign];

// Identifies which screen of the submit-quote landing sent the carrier into the
// app. Carried as the `handoff_screen` property on
// QUOTE_COMMAND_LINK_MANAGE_QUOTE_CLICKED, which fires from all three: the event
// name predates the post-submission and recoverable-outcome screens, so this
// property is the only thing that tells the three apart — `existing_quote_state`
// reads "none" on two of them.
export const HandoffScreen = {
  QUOTE_SUBMITTED: "quote_submitted",
  EXISTING_QUOTE: "existing_quote",
  RECOVERABLE_OUTCOME: "recoverable_outcome",
} as const;
export type HandoffScreen = (typeof HandoffScreen)[keyof typeof HandoffScreen];

// The campaign each handoff screen tags its deep link with. Derived rather than
// passed alongside the screen so the event and the UTM cannot disagree.
export const HANDOFF_SCREEN_UTM_CAMPAIGNS: Record<
  HandoffScreen,
  CommandLinkUtmCampaign
> = {
  [HandoffScreen.QUOTE_SUBMITTED]:
    CommandLinkUtmCampaign.SUBMIT_QUOTE_SUBMITTED,
  [HandoffScreen.EXISTING_QUOTE]: CommandLinkUtmCampaign.SUBMIT_QUOTE_MANAGE,
  [HandoffScreen.RECOVERABLE_OUTCOME]:
    CommandLinkUtmCampaign.SUBMIT_QUOTE_MANAGE,
};

export const ActivationFlow = {
  VOLUNTELL: "voluntell",
  VOLUNTELL_COMMAND_LINK: "voluntell-command-link",
  COMPANY_MEMBER: "company-member-activation",
} as const;
export type ActivationFlow =
  (typeof ActivationFlow)[keyof typeof ActivationFlow];

export function isUserAccountActivationFlow(
  value: string | null,
): value is ActivationFlow {
  return (
    value !== null &&
    (Object.values(ActivationFlow) as string[]).includes(value)
  );
}

export const MAX_TFC_LINE_ITEMS = 10;
export const MAX_TFC_LINE_ITEM_DESCRIPTION_LENGTH = 255;
export const MAX_TFC_NOTES_LENGTH = 1024;

// ShipmentDelivery.unit_of_measure_quantity is a numeric(9, 4) column on the
// backend, so 99999.9999 is the largest delivered quantity it can store.
export const MAX_UNIT_OF_MEASURE_QUANTITY = 99999.9999;
export const UNIT_OF_MEASURE_QUANTITY_DECIMAL_PLACES = 4;

export const MFA_CODE_LENGTH = 6;
export const MFA_CODE_EXPIRY_MINUTES = 3;

export const ShipmentMicroReviewSource = {
  EMAIL_LINK: "email-link",
  IN_APP_SHIPMENT_DETAIL: "in-app-shipment-detail",
} as const;
export type ShipmentMicroReviewSource =
  (typeof ShipmentMicroReviewSource)[keyof typeof ShipmentMicroReviewSource];

// Keep in sync with application/company/models.py CompanyReviewSource
export const CompanyReviewSource = {
  CARRIER_PROFILE: "CARRIER_PROFILE",
  SHIPMENT_DETAIL_MICRO_REVIEW: "SHIPMENT_DETAIL_MICRO_REVIEW",
  BOOKING_CANCELLATION: "BOOKING_CANCELLATION",
} as const;
export type CompanyReviewSource =
  (typeof CompanyReviewSource)[keyof typeof CompanyReviewSource];

// Keep in sync with application/network/models.py FollowShipperRequestSource.
// Only the sources a client can send belong here: the backend enum also holds
// server-set sources (e.g. CREATE_SHIPMENT_FORM_VISIBILITY_SECTION) that no
// frontend caller ever passes, so this list is intentionally shorter.
export const FollowShipperRequestSource = {
  CHAT_CONVERSATION_HEADER: "CHAT_CONVERSATION_HEADER",
  SHIPMENT_QUOTE_DETAIL: "SHIPMENT_QUOTE_DETAIL",
  SHIPMENT_QUOTE_TABLE_ROW: "SHIPMENT_QUOTE_TABLE_ROW",
  SHIPMENT_PROGRESS_DETAIL: "SHIPMENT_PROGRESS_DETAIL",
  ADMIN_ON_BEHALF_OF_SHIPPER: "ADMIN_ON_BEHALF_OF_SHIPPER",
  EMAIL_INPUT_NETWORK_PAGE: "EMAIL_INPUT_NETWORK_PAGE",
  SHIPMENT_MICRO_REVIEW: "SHIPMENT_MICRO_REVIEW",
  CARRIER_PROFILE_TEAM: "CARRIER_PROFILE_TEAM",
} as const;
export type FollowShipperRequestSource =
  (typeof FollowShipperRequestSource)[keyof typeof FollowShipperRequestSource];

// Minimum digit count for a valid E.164 phone number (e.g. +15551234567 = 11 digits)
export const E164_MIN_DIGIT_COUNT = 11;

// Shipment types that have no public tracking page — the public endpoint
// (GET /public/shipments/<token>) returns 404 for these, so a public link can
// never be shared for them. Keep in sync with the unsupported-type set in
// application/shipment/state_service/condensed_shipment_service.py
// (UnsupportedShipmentTypeForPublicAccessException).
export const NON_PUBLICLY_SHAREABLE_SHIPMENT_TYPES: ShipmentType[] = [
  "QUOTES_ONLY",
  "MULTI_SHIPMENT_PROJECT",
];
